import type { PerDishMetrics } from '../../utils/perDishCalculations';
import {
  getLatestCompletedMonth,
  getMonthRange,
  toMonth,
  type GoalActualPoint,
  type GoalChartRange,
} from './goalChartProjection';

export type GoalPerDishValueKey =
  | 'kwh_per_dish'
  | 'm3_water_per_dish'
  | 'm3_gas_per_dish'
  | 'kg_general_waste_per_dish'
  | 'kg_co2e_per_dish';

function formatMonthName(month: string): string {
  return new Intl.DateTimeFormat('es-ES', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${month}-01T00:00:00Z`));
}

export function clipPerDishMetricsToCompletedMonths(
  data: readonly PerDishMetrics[],
  now = new Date(),
): PerDishMetrics[] {
  const latestCompletedMonth = getLatestCompletedMonth(now);
  return data.filter(
    (month) =>
      `${month.year}-${String(month.month).padStart(2, '0')}` <=
      latestCompletedMonth,
  );
}

export function padPerDishMetricsToRange(
  data: readonly PerDishMetrics[],
  range: GoalChartRange,
): PerDishMetrics[] {
  const byMonth = new Map(
    data.map((month) => [
      `${month.year}-${String(month.month).padStart(2, '0')}`,
      month,
    ]),
  );

  return getMonthRange(range.startMonth, range.endMonth).flatMap((month) => {
    const existing = byMonth.get(month);
    if (existing) return [existing];
    const [yearPart, monthPart] = month.split('-');
    const year = Number(yearPart);
    const monthNumber = Number(monthPart);
    if (!Number.isFinite(year) || !Number.isFinite(monthNumber)) return [];
    return [
      {
        year,
        month: monthNumber,
        month_name: formatMonthName(month),
        has_sales_data: false,
      },
    ];
  });
}

export function getPerDishActuals(
  data: readonly PerDishMetrics[],
  valueKey: GoalPerDishValueKey,
): GoalActualPoint[] {
  return data.map((month) => ({
    date: `${month.year}-${String(month.month).padStart(2, '0')}`,
    value: month[valueKey],
  }));
}

export function isMonthCompleted(month: string, now = new Date()): boolean {
  return toMonth(month) <= getLatestCompletedMonth(now);
}
